import { useState, useCallback } from "react"
import { lumi } from "../lib/lumi"

// ==================== 타입 정의 ====================

export type MilestoneStatus =
  | "todo"
  | "in_progress"
  | "review"
  | "completed"
  | "archive"
  | "stuck"

/** 마일스톤 세부 To do 체크리스트 항목 */
export interface MilestoneTodo {
  id: string
  text: string
  done: boolean
  doneAt?: string
  doneByName?: string
}

/** 마일스톤 담당자 (등록된 활성 사용자 중 복수 선택) */
export interface MilestoneAssignee {
  userId: string
  userName: string
  email?: string
  teamName?: string
}

export interface Milestone {
  _id: string
  projectId: string
  projectName?: string
  title: string
  deliverable?: string
  description?: string
  todos?: MilestoneTodo[]
  startDate: string
  dueDate: string
  slackDays?: number
  status: MilestoneStatus
  progress?: number
  assignees?: MilestoneAssignee[]
  assigneeId?: string
  assigneeName?: string
  sortOrder?: number
  completedAt?: string
  creator: string
  creatorName?: string
  createdAt: string
  updatedAt: string
}

export interface MilestoneInput {
  projectId: string
  projectName?: string
  title: string
  deliverable?: string
  description?: string
  todos?: MilestoneTodo[]
  startDate: string
  dueDate: string
  slackDays?: number
  status: MilestoneStatus
  progress?: number
  assignees?: MilestoneAssignee[]
  assigneeId?: string
  assigneeName?: string
  sortOrder?: number
}

/** 마일스톤 담당자 목록 (레거시 assigneeName 문자열도 함께 처리) */
export function getAssignees(m: {
  assignees?: MilestoneAssignee[]
  assigneeId?: string
  assigneeName?: string
}): MilestoneAssignee[] {
  if (m.assignees && m.assignees.length > 0) return m.assignees
  const legacy = (m.assigneeName || "").trim()
  if (!legacy) return []
  return legacy
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ userId: m.assigneeId || "", userName: name }))
}

/** 담당자 목록을 표시용 문자열로 변환 */
export function formatAssignees(list: MilestoneAssignee[]): string {
  return list.map((a) => a.userName).join(", ")
}

/** 특정 사용자가 담당자인지 확인 (userId 우선, 이름 보조) */
export function isAssignedTo(
  m: { assignees?: MilestoneAssignee[]; assigneeId?: string; assigneeName?: string },
  userId?: string,
  userName?: string
): boolean {
  const list = getAssignees(m)
  if (list.length === 0) return false
  return list.some((a) => {
    if (userId && a.userId && a.userId === userId) return true
    if (!a.userId && userName && a.userName === userName) return true
    return false
  })
}

/** 체크리스트 항목용 고유 ID 생성 */
export function createTodoId(): string {
  return `td_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

/** 체크리스트 완료율 (0~100). 항목이 없으면 null */
export function getTodoProgress(todos?: MilestoneTodo[]): number | null {
  if (!todos || todos.length === 0) return null
  const done = todos.filter((t) => t.done).length
  return Math.round((done / todos.length) * 100)
}

/**
 * 세부 To do 기준 진행률 자동 계산.
 * - 체크리스트가 있으면 완료 비율(모두 완료 시 100)을 진행률로 사용
 * - 체크리스트가 없으면 수동 입력값(fallback)을 그대로 유지
 */
export function deriveProgress(todos?: MilestoneTodo[], fallback?: number): number {
  const todoProgress = getTodoProgress(todos)
  if (todoProgress !== null) return todoProgress
  return Math.min(100, Math.max(0, Number(fallback ?? 0)))
}

// ==================== 상태 메타 (칸반 컬럼 + 배지 공용) ====================

export const STATUS_META: Record<
  MilestoneStatus,
  { label: string; bar: string; chip: string; dot: string; border: string }
> = {
  todo: {
    label: "작업필요",
    bar: "bg-gray-400",
    chip: "bg-gray-100 text-gray-700",
    dot: "bg-gray-400",
    border: "border-gray-300",
  },
  in_progress: {
    label: "작업중",
    bar: "bg-green-500",
    chip: "bg-green-100 text-green-700",
    dot: "bg-green-500",
    border: "border-green-400",
  },
  review: {
    label: "검토필요",
    bar: "bg-amber-500",
    chip: "bg-amber-100 text-amber-700",
    dot: "bg-amber-500",
    border: "border-amber-400",
  },
  completed: {
    label: "완료",
    bar: "bg-blue-500",
    chip: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
    border: "border-blue-400",
  },
  archive: {
    label: "보관",
    bar: "bg-slate-400",
    chip: "bg-slate-100 text-slate-600",
    dot: "bg-slate-400",
    border: "border-slate-300",
  },
  stuck: {
    label: "이슈발생",
    bar: "bg-red-500",
    chip: "bg-red-100 text-red-700",
    dot: "bg-red-500",
    border: "border-red-400",
  },
}

// 칸반 컬럼 표시 순서
export const KANBAN_COLUMNS: MilestoneStatus[] = [
  "todo",
  "in_progress",
  "review",
  "completed",
  "stuck",
  "archive",
]

// ==================== 날짜 유틸 ====================

/** YYYY-MM-DD (date input 값) → ISO datetime 문자열 */
export function dateInputToIso(value: string): string {
  if (!value) return ""
  return new Date(`${value}T00:00:00.000Z`).toISOString()
}

/** ISO datetime → YYYY-MM-DD (date input 값) */
export function isoToDateInput(value?: string): string {
  if (!value) return ""
  return value.slice(0, 10)
}

/** 두 날짜 사이 일수 (b - a) */
export function diffDays(a: string, b: string): number {
  const start = new Date(a.slice(0, 10)).getTime()
  const end = new Date(b.slice(0, 10)).getTime()
  return Math.round((end - start) / 86400000)
}

/** 날짜에 일수를 더한 ISO 문자열 */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso.slice(0, 10))
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString()
}

/** 화면 표시용 짧은 날짜 (M/D) */
export function formatShortDate(iso?: string): string {
  if (!iso) return "-"
  const d = new Date(iso.slice(0, 10))
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`
}

/** 화면 표시용 날짜 (YYYY.MM.DD) */
export function formatDate(iso?: string): string {
  if (!iso) return "-"
  const d = iso.slice(0, 10).split("-")
  return `${d[0]}.${d[1]}.${d[2]}`
}

// ==================== 훅 ====================

export function useMilestones() {
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [loading, setLoading] = useState(false)

  /** 마일스톤 목록 조회 (프로젝트 필터 옵션) */
  const fetchMilestones = useCallback(async (projectId?: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.project_milestones.list({
        filter: projectId ? { projectId } : {},
        sort: { startDate: 1 },
      })
      setMilestones((list || []) as Milestone[])
    } catch (error) {
      console.error("마일스톤 조회 실패:", error)
      setMilestones([])
    } finally {
      setLoading(false)
    }
  }, [])

  /** 마일스톤 생성 */
  const createMilestone = useCallback(async (input: MilestoneInput, creator: string, creatorName?: string) => {
    const now = new Date().toISOString()
    const assignees = input.assignees || []
    const todos = input.todos || []
    const created = await lumi.entities.project_milestones.create({
      ...input,
      todos,
      assignees,
      assigneeId: assignees[0]?.userId || "",
      assigneeName: assignees.map((a) => a.userName).join(", "),
      slackDays: Number(input.slackDays ?? 0),
      // 세부 To do가 있으면 완료 비율을 진행률로 자동 반영
      progress: deriveProgress(todos, input.progress),
      creator,
      creatorName: creatorName || "",
      createdAt: now,
      updatedAt: now,
    })
    return created
  }, [])

  /** 마일스톤 수정 (ID 먼저, 데이터 나중) */
  const updateMilestone = useCallback(async (id: string, updates: Partial<MilestoneInput> & { completedAt?: string }) => {
    const payload: Record<string, any> = { ...updates, updatedAt: new Date().toISOString() }
    if (updates.assignees !== undefined) {
      const assignees = updates.assignees || []
      payload.assignees = assignees
      payload.assigneeId = assignees[0]?.userId || ""
      payload.assigneeName = assignees.map((a) => a.userName).join(", ")
    }
    if (updates.slackDays !== undefined) payload.slackDays = Number(updates.slackDays)
    if (updates.progress !== undefined) payload.progress = Number(updates.progress)
    // 세부 To do가 함께 갱신되면 완료 비율로 진행률을 자동 계산
    if (updates.todos !== undefined) {
      payload.progress = deriveProgress(updates.todos, updates.progress)
    }
    const updated = await lumi.entities.project_milestones.update(id, payload)
    return updated
  }, [])

  /** 마일스톤 삭제 (문자열 ID만) */
  const deleteMilestone = useCallback(async (id: string) => {
    await lumi.entities.project_milestones.delete(id)
  }, [])

  /**
   * 세부 To do 항목 완료/해제 토글.
   * 낙관적 업데이트로 즉시 취소선을 반영한 뒤 서버에 저장한다.
   */
  const toggleTodo = useCallback(
    async (milestone: Milestone, todoId: string, userName?: string) => {
      const now = new Date().toISOString()
      const nextTodos = (milestone.todos || []).map((t) =>
        t.id === todoId
          ? {
              ...t,
              done: !t.done,
              doneAt: !t.done ? now : "",
              doneByName: !t.done ? userName || "" : "",
            }
          : t
      )

      // 세부 To do 완료 비율로 진행률 자동 계산 (모두 완료 시 100%)
      const nextProgress = deriveProgress(nextTodos, milestone.progress)
      const allDone = nextTodos.length > 0 && nextTodos.every((t) => t.done)

      // 낙관적 반영
      setMilestones((prev) =>
        prev.map((m) =>
          m._id === milestone._id ? { ...m, todos: nextTodos, progress: nextProgress } : m
        )
      )

      try {
        const payload: Record<string, any> = {
          todos: nextTodos,
          progress: nextProgress,
          updatedAt: now,
        }
        // 세부 항목이 모두 완료되면 완료 일시를 기록하고, 해제되면 비운다
        payload.completedAt = allDone ? now : ""
        await lumi.entities.project_milestones.update(milestone._id, payload)
        return nextTodos
      } catch (error) {
        // 실패 시 원복
        setMilestones((prev) =>
          prev.map((m) =>
            m._id === milestone._id
              ? { ...m, todos: milestone.todos || [], progress: milestone.progress }
              : m
          )
        )
        throw error
      }
    },
    []
  )

  return {
    milestones,
    loading,
    fetchMilestones,
    createMilestone,
    updateMilestone,
    deleteMilestone,
    toggleTodo,
  }
}
